/**
 * "Sources & values" drawer rendered inside an expanded pick row.
 *
 * Lays out every number that contributes to the row's headline KPIs, grouped
 * by where it came from, with the formula that produced it. The goal is to
 * make it obvious — at a glance — that no number is invented; each one is
 * either raw data from a named API or a derivation we can show on one line.
 */

import type { DoublePick, ProbabilityPick } from "../../shared/types";
import { formatPercent, formatSignedPercent, formatStake } from "../lib/format";

export function PickProvenance({ pick }: { pick: ProbabilityPick }) {
  const consensus = pick.consensusFairProbability;

  return (
    <div className="space-y-2.5 border-t border-ink-100 bg-ink-50/40 px-3 py-3 text-xs">
      <ProvenanceSection title="Norsk Tipping (live odds endpoint)">
        <Row
          name="Decimal odds"
          value={pick.decimalOdds.toFixed(2)}
          formula={`raw payout per €1 staked on "${pick.selectionName}"`}
        />
        <Row
          name="Implied probability"
          value={formatPercent(pick.impliedProbability)}
          formula="1 / decimal odds — the price expressed as a probability before removing margin"
        />
        <Row
          name="Bookmaker margin"
          value={formatPercent(pick.match.margin)}
          formula="Σ(1/odds) − 1 across home / draw / away — NT's mark-up on this market"
        />
      </ProvenanceSection>

      <ProvenanceSection title="Derived (no extra source)">
        <Row
          name="No-vig probability"
          value={formatPercent(pick.fairProbability)}
          formula="implied probability / (1 + margin) — NT's estimate with the bookmaker's profit stripped out"
        />
        <Row
          name="Fair odds"
          value={pick.fairOdds.toFixed(2)}
          formula="1 / no-vig probability — what the odds would be if NT took zero profit"
        />
      </ProvenanceSection>

      {consensus !== undefined ? (
        <ProvenanceSection title="the-odds-api consensus (sharp books)">
          <Row
            name="Consensus probability"
            value={formatPercent(consensus)}
            formula="no-vig probability from Pinnacle when available, otherwise median across Betfair Exchange / Smarkets / Matchbook"
          />
          <Row
            name="Expected value"
            value={pick.evScore !== undefined ? formatSignedPercent(pick.evScore) : "—"}
            formula="consensus probability × NT odds − 1. Positive = NT is mispricing in your favour."
          />
        </ProvenanceSection>
      ) : (
        <ProvenanceSection title="the-odds-api consensus (sharp books)">
          <p className="text-[11px] leading-snug text-ink-500">
            No matching consensus event for this fixture. Either the competition isn&apos;t covered by our
            sport-key map, or team names didn&apos;t fuzzy-match within edit distance 2. Without consensus we
            can&apos;t compute an honest EV.
          </p>
        </ProvenanceSection>
      )}

      {pick.modelInputs ? (
        <ProvenanceSection title="Independent model (Dixon-Coles, goals-bootstrap)">
          <Row
            name="λ home"
            value={pick.modelInputs.lambdaHome.toFixed(2)}
            formula="expected goals home = league avg × Elo factor × form factor. The Poisson rate that feeds the score grid."
          />
          <Row
            name="λ away"
            value={pick.modelInputs.lambdaAway.toFixed(2)}
            formula="expected goals away (same formula, mirrored)"
          />
          {pick.modelFairProbability !== undefined ? (
            <Row
              name="Model probability"
              value={formatPercent(pick.modelFairProbability)}
              formula="Dixon-Coles bivariate Poisson on (λ_home, λ_away) with ρ = -0.18, summed over the chosen outcome"
            />
          ) : null}
          {pick.modelEv !== undefined ? (
            <Row
              name="Model EV"
              value={formatSignedPercent(pick.modelEv)}
              formula="model probability × NT odds − 1. This is an independent signal from the consensus EV above."
            />
          ) : null}
          <Row
            name="Inputs confidence"
            value={formatPercent(pick.modelInputs.confidence)}
            formula="0.25 each for known home Elo / away Elo / home form / away form. < 0.5 = warming up."
          />
          {pick.highConviction ? (
            <p className="rounded bg-emerald-50 px-2 py-1.5 text-[11px] leading-snug text-emerald-800">
              <strong>High-conviction pick.</strong> Model EV and consensus EV are both positive, and the two
              probability estimates agree within 4 percentage points. Two independent signals pointing the
              same way is the best-evidence setup for a +EV bet.
            </p>
          ) : null}
        </ProvenanceSection>
      ) : (
        <ProvenanceSection title="Independent model (Dixon-Coles)">
          <p className="text-[11px] leading-snug text-ink-500">
            Model warming up — we couldn&apos;t resolve either team&apos;s Elo (clubelo.com) or recent form
            (football-data.org). Once live xG data accumulates from the daily cron (~2 weeks), the model will
            fire reliably for more matches.
          </p>
        </ProvenanceSection>
      )}

      {pick.modelInputs &&
      (pick.modelInputs.eloHome !== undefined ||
        pick.modelInputs.eloAway !== undefined ||
        pick.modelInputs.formHome !== undefined ||
        pick.modelInputs.formAway !== undefined) ? (
        <ProvenanceSection title="Strength signals">
          {pick.modelInputs.eloHome !== undefined ? (
            <Row
              name={`Elo ${pick.match.homeTeam}`}
              value={pick.modelInputs.eloHome.toFixed(0)}
              formula="clubelo.com daily ranking (no auth)"
            />
          ) : null}
          {pick.modelInputs.eloAway !== undefined ? (
            <Row
              name={`Elo ${pick.match.awayTeam}`}
              value={pick.modelInputs.eloAway.toFixed(0)}
              formula="clubelo.com daily ranking"
            />
          ) : null}
          {pick.modelInputs.formHome !== undefined ? (
            <Row
              name={`Form ${pick.match.homeTeam}`}
              value={formatPercent(pick.modelInputs.formHome)}
              formula="last 5 league matches points-rate (football-data.org standings)"
            />
          ) : null}
          {pick.modelInputs.formAway !== undefined ? (
            <Row
              name={`Form ${pick.match.awayTeam}`}
              value={formatPercent(pick.modelInputs.formAway)}
              formula="last 5 league matches points-rate"
            />
          ) : null}
        </ProvenanceSection>
      ) : null}

      {pick.weather ? (
        <ProvenanceSection title="Weather at kickoff">
          <Row
            name="Temperature"
            value={`${pick.weather.temperatureC.toFixed(1)} °C`}
            formula="OpenWeather forecast for the country's capital — best-effort approximation of conditions in the region."
          />
          <Row
            name="Wind speed"
            value={`${pick.weather.windSpeedMs.toFixed(1)} m/s`}
            formula="3-hour forecast block closest to kickoff time"
          />
          <Row
            name="Precipitation"
            value={`${pick.weather.precipMm.toFixed(1)} mm / 3h`}
            formula="rain + snow expected in the kickoff-window"
          />
          <Row
            name="Conditions"
            value={pick.weather.conditionDescription}
            formula={`severity ${formatPercent(pick.weather.severity)} — only flagged on the row when ≥ 40%`}
          />
        </ProvenanceSection>
      ) : null}

      <ProvenanceSection title="Whitelist match">
        <Row
          name="League"
          value={pick.leagueId ?? "—"}
          formula={`matched from "${pick.match.league}" against the curated whitelist (src/shared/leagues.ts)`}
        />
        <Row
          name="Kickoff (Oslo)"
          value={new Date(pick.match.kickoffTime).toLocaleString("en-GB", {
            timeZone: "Europe/Oslo",
            weekday: "short",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
          formula="from the NT event payload"
        />
      </ProvenanceSection>
    </div>
  );
}

export function DoubleProvenance({ double }: { double: DoublePick }) {
  const [legA, legB] = double.legs;
  const consensusA = legA.consensusFairProbability;
  const consensusB = legB.consensusFairProbability;
  const hasBothConsensus = consensusA !== undefined && consensusB !== undefined;

  return (
    <div className="space-y-2.5 border-t border-ink-100 bg-ink-50/40 px-3 py-3 text-xs">
      <ProvenanceSection title="Leg A — Norsk Tipping">
        <Row name="Selection" value={legA.selectionName} formula={legA.match.league} />
        <Row
          name="Decimal odds"
          value={legA.decimalOdds.toFixed(2)}
          formula={`raw payout, ${legA.outcome} outcome`}
        />
        <Row
          name="No-vig probability"
          value={formatPercent(legA.fairProbability)}
          formula="NT 1X2 prices with bookmaker margin stripped"
        />
      </ProvenanceSection>

      <ProvenanceSection title="Leg B — Norsk Tipping">
        <Row name="Selection" value={legB.selectionName} formula={legB.match.league} />
        <Row
          name="Decimal odds"
          value={legB.decimalOdds.toFixed(2)}
          formula={`raw payout, ${legB.outcome} outcome`}
        />
        <Row
          name="No-vig probability"
          value={formatPercent(legB.fairProbability)}
          formula="NT 1X2 prices with bookmaker margin stripped"
        />
      </ProvenanceSection>

      <ProvenanceSection title="Combined (derived)">
        <Row
          name="Combined odds"
          value={double.combinedDecimalOdds.toFixed(2)}
          formula={`${legA.decimalOdds.toFixed(2)} × ${legB.decimalOdds.toFixed(2)}`}
        />
        <Row
          name="Joint fair probability"
          value={formatPercent(double.combinedFairProbability)}
          formula="P(A) × P(B), independence assumption (backtest correlation ratio ≈ 1.00, so safe)"
        />
        <Row
          name="Combined fair odds"
          value={double.combinedFairOdds.toFixed(2)}
          formula="1 / joint fair probability"
        />
        <Row
          name="Combined EV (NT-only)"
          value={formatSignedPercent(double.combinedEv)}
          formula="joint fair probability × combined odds − 1"
        />
      </ProvenanceSection>

      <ProvenanceSection title="Consensus (the-odds-api)">
        {hasBothConsensus ? (
          <>
            <Row
              name="Leg A consensus probability"
              value={formatPercent(consensusA!)}
              formula="sharp-book consensus for the chosen outcome on leg A"
            />
            <Row
              name="Leg B consensus probability"
              value={formatPercent(consensusB!)}
              formula="sharp-book consensus for the chosen outcome on leg B"
            />
            <Row
              name="Combined consensus EV"
              value={
                double.combinedConsensusEv !== undefined
                  ? formatSignedPercent(double.combinedConsensusEv)
                  : "—"
              }
              formula="P(A)_consensus × P(B)_consensus × combined odds − 1"
            />
          </>
        ) : (
          <p className="text-[11px] leading-snug text-ink-500">
            Consensus missing for {consensusA === undefined ? "leg A" : "leg B"}
            {consensusA === undefined && consensusB === undefined ? " and leg B" : ""}. We fall back to the
            NT-only combined EV above for sorting and Kelly sizing.
          </p>
        )}
      </ProvenanceSection>

      <ProvenanceSection title="Stake recommendation">
        <Row
          name="Kelly stake"
          value={formatStake(double.kellyStakeFraction)}
          formula="0.15 × full Kelly = 0.15 × (p · b − q) / b, where b = combined odds − 1, p = joint probability (consensus if available), q = 1 − p. Capped well below full Kelly because p itself is an estimate."
        />
      </ProvenanceSection>
    </div>
  );
}

function ProvenanceSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">{title}</p>
      <div className="mt-1 space-y-1.5">{children}</div>
    </section>
  );
}

function Row({ name, value, formula }: { name: string; value: string; formula: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold text-ink-900">{name}</span>
        <span className="font-mono text-ink-900">{value}</span>
      </div>
      <p className="text-[11px] leading-snug text-ink-500">{formula}</p>
    </div>
  );
}
